// `useReconnectAccount()` — US-171 reconnect dispatcher.
//
// Two simultaneous side-effects:
//   1. `window.postMessage({ type: 'ls.reconnect', accountId }, origin)` —
//      the postmessage-bridge content script (registered on app.barel.<tld>
//      and localhost:5180) forwards this to the extension SW, which opens
//      a foregrounded LinkedIn tab and flips local health to `reconnecting`.
//   2. `useReconnectLinkedinAccount` mutation — POST audit row.
//
// The hook intentionally does NOT track which account is currently
// reconnecting. Callers maintain their own `reconnectingId` state so the
// AccountsListPage can render the transient pill exactly where the user
// clicked.

import { useCallback } from 'react';

import { useReconnectLinkedinAccount } from '@barel/api-client/react';

export interface UseReconnectAccountResult {
  /** Trigger reconnect for the given account. Fire-and-forget — failures
   *  in either side-effect are swallowed (the SW logs locally). */
  reconnect: (accountId: string) => void;
  /** True while the audit POST is in flight. The postMessage path is
   *  synchronous so it always resolves immediately. */
  isPending: boolean;
}

export function useReconnectAccount(): UseReconnectAccountResult {
  const mutation = useReconnectLinkedinAccount();
  const reconnect = useCallback(
    (accountId: string) => {
      // Same-window postMessage — the postmessage-bridge content script
      // listens on `window` and matches event.origin against its allow-list,
      // so we MUST target the current origin precisely.
      if (typeof window !== 'undefined') {
        try {
          window.postMessage({ type: 'ls.reconnect', accountId }, window.location.origin);
        } catch {
          // Non-browser context (SSR / tests with no window) — ignored.
        }
      }
      mutation.mutate({ id: accountId });
    },
    [mutation],
  );
  return { reconnect, isPending: mutation.isPending };
}
